import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import SingleSelect from "@/components/controls/SingleSelect";
import {
  MultiSelect,
  multiSelectionOptions
} from "@/components/controls/MultiSelect";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";
import Section from "@/pages/settings/components/sections/Section";
import {
  isApolloNotConnected,
  useApolloCallOutcomes,
  useApolloLists,
  useApolloSettings,
  useSaveApolloContactImport,
  useSaveApolloDialer,
  useSaveApolloListSync
} from "@/entities/apollo/hooks";
import { ApolloListSyncSettings } from "@/entities/apollo/types";
import {
  APOLLO_SETTINGS_TRANSLATION_PREFIX as PREFIX,
  callOutcomeOptions,
  listIdOptions,
  listNameOptions,
  sameValues
} from "@/pages/settings/components/integrations/apollo/apolloSettingsOptions";

type ListSyncListField = Exclude<keyof ApolloListSyncSettings, "enabled">;

const LIST_SYNC_FIELDS: ListSyncListField[] = [
  "activeCustomersList",
  "churnedCustomersList",
  "activeDealsAndLeadsList"
];

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-[280px] min-h-8 h-9";
const MULTI_SELECT_CLASSNAME = "w-[280px] border-border-border2 min-h-8 h-9";

interface DeferredMultiSelectProps {
  fieldName: string;
  values: string[];
  options: multiSelectionOptions;
  placeholder: string;
  disabled: boolean;
  isLoading: boolean;
  onSave: (values: string[], onFailed: () => void) => void;
}

// Saves once the picker closes, so ticking several options is one write.
// A chip removed while it's closed is saved straight away. The picked values
// stay on screen until the saved ones arrive, and a failed save puts the
// saved ones back.
const DeferredMultiSelect: React.FC<DeferredMultiSelectProps> = ({
  fieldName,
  values,
  options,
  placeholder,
  disabled,
  isLoading,
  onSave
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(values);
  const [syncedValues, setSyncedValues] = useState(values);

  if (!sameValues(values, syncedValues)) {
    setSyncedValues(values);
    if (!isOpen) setDraft(values);
  }

  const commit = (next: string[]) => {
    if (!sameValues(next, values)) onSave(next, () => setDraft(values));
  };

  return (
    <MultiSelect
      data-test={`apollo-settings-${fieldName}`}
      aria-label={fieldName}
      selectedValues={draft}
      options={options}
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) commit(draft);
      }}
      onValueChange={(next) => {
        setDraft(next);
        if (!isOpen) commit(next);
      }}
      placeholder={placeholder}
      placeholderClassName="text-tertiary"
      activeClassName="!border-primary"
      className={MULTI_SELECT_CLASSNAME}
      maxCount={2}
      showSearch
      disabled={disabled}
      isLoading={isLoading}
    />
  );
};

export const ApolloSettings: React.FC = () => {
  const { t } = useTranslation();
  const settingsQuery = useApolloSettings();
  const accountListsQuery = useApolloLists("accounts");
  const contactListsQuery = useApolloLists("contacts");
  const callOutcomesQuery = useApolloCallOutcomes();
  const saveListSync = useSaveApolloListSync();
  const saveDialer = useSaveApolloDialer();
  const saveContactImport = useSaveApolloContactImport();

  // The backend refuses saves from anyone but a Settings admin.
  const isSettingsAdmin = useCanAccessAdminSettings();

  const settings = settingsQuery.data;
  const isSaving =
    saveListSync.isPending ||
    saveDialer.isPending ||
    saveContactImport.isPending;
  const controlsDisabled = !settings || isSaving || !isSettingsAdmin;

  const showSaveError = () => {
    toast.error(t(`${PREFIX}.updateError`));
  };
  const onSaveError = { onError: showSaveError };
  const onDeferredSaveError = (onFailed: () => void) => ({
    onError: () => {
      onFailed();
      showSaveError();
    }
  });

  // Lists and outcomes come from Apollo itself, so they fail on their own.
  const apolloSubtitle = (
    query: { error: unknown; isError: boolean },
    subtitleKey: string
  ) => {
    if (isApolloNotConnected(query.error)) return t(`${PREFIX}.notConnected`);
    if (query.isError) return t(`${PREFIX}.apolloLoadError`);
    return t(subtitleKey);
  };

  const sectionSubtitle = (key: string) =>
    settingsQuery.isError ? t(`${PREFIX}.loadError`) : t(key);

  const listSync = settings?.listSync;
  const dialer = settings?.dialer;
  const contactImport = settings?.contactImport;

  const updateListSync = (changes: Partial<ApolloListSyncSettings>) => {
    if (!listSync) return;
    saveListSync.mutate({ data: { ...listSync, ...changes } }, onSaveError);
  };

  const listStatusLabel = (status: "missing" | "ambiguous") =>
    t(`${PREFIX}.listSync.listStatus.${status}`);

  return (
    <div className="flex w-full flex-col gap-12" data-test="apollo-settings">
      {!isSettingsAdmin && (
        <Text variant="bodySecondary" data-test="apollo-settings-admin-only">
          {t(`${PREFIX}.adminOnly`)}
        </Text>
      )}
      <Section
        title={t(`${PREFIX}.contactImport.title`)}
        subTitle={sectionSubtitle(`${PREFIX}.contactImport.subtitle`)}
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.contactImport.lists.title`),
            subTitle: apolloSubtitle(
              contactListsQuery,
              `${PREFIX}.contactImport.lists.subtitle`
            ),
            customAction: (
              <DeferredMultiSelect
                fieldName="contactImportLists"
                values={contactImport?.labelIds ?? []}
                options={listIdOptions(
                  contactListsQuery.data ?? [],
                  contactImport?.labelIds ?? []
                )}
                placeholder={t(`${PREFIX}.contactImport.lists.placeholder`)}
                disabled={controlsDisabled}
                isLoading={contactListsQuery.isLoading}
                onSave={(labelIds, onFailed) =>
                  // A picker can still commit on Escape after it's disabled.
                  isSettingsAdmin &&
                  contactImport &&
                  saveContactImport.mutate(
                    { data: { ...contactImport, labelIds } },
                    onDeferredSaveError(onFailed)
                  )
                }
              />
            )
          },
          {
            title: t(`${PREFIX}.contactImport.createLeads.title`),
            subTitle: t(`${PREFIX}.contactImport.createLeads.subtitle`),
            customAction: (
              <Switch
                data-test="apollo-settings-createLeadsFromList"
                aria-label="createLeadsFromList"
                checked={contactImport?.createLeadsFromList ?? false}
                disabled={controlsDisabled}
                onCheckedChange={(createLeadsFromList) =>
                  contactImport &&
                  saveContactImport.mutate(
                    { data: { ...contactImport, createLeadsFromList } },
                    onSaveError
                  )
                }
              />
            )
          }
        ]}
      />
      <Section
        title={t(`${PREFIX}.listSync.title`)}
        subTitle={sectionSubtitle(`${PREFIX}.listSync.subtitle`)}
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.listSync.enabled.title`),
            subTitle: t(`${PREFIX}.listSync.enabled.subtitle`),
            customAction: (
              <Switch
                data-test="apollo-settings-listSyncEnabled"
                aria-label="listSyncEnabled"
                checked={listSync?.enabled ?? false}
                disabled={controlsDisabled}
                onCheckedChange={(enabled) => updateListSync({ enabled })}
              />
            )
          },
          ...LIST_SYNC_FIELDS.map((field) => ({
            title: t(`${PREFIX}.listSync.lists.${field}.title`),
            subTitle: apolloSubtitle(
              accountListsQuery,
              `${PREFIX}.listSync.lists.${field}.subtitle`
            ),
            customAction: (
              <SingleSelect
                fieldName={field}
                options={listNameOptions(
                  accountListsQuery.data ?? [],
                  listSync?.[field] ?? "",
                  listStatusLabel
                )}
                selectedValue={listSync?.[field]}
                onSelection={(value) => {
                  const name = String(value);
                  if (name && name !== listSync?.[field]) {
                    updateListSync({ [field]: name });
                  }
                }}
                formatter={(value) => String(value)}
                disabled={controlsDisabled}
                isLoading={accountListsQuery.isLoading}
                showSearch
                showSelectedItemSubtitle
                className={SELECT_CLASSNAME}
                popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                variant="form"
              />
            )
          }))
        ]}
      />
      <Section
        title={t(`${PREFIX}.dialer.title`)}
        subTitle={sectionSubtitle(`${PREFIX}.dialer.subtitle`)}
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.dialer.enabled.title`),
            subTitle: t(`${PREFIX}.dialer.enabled.subtitle`),
            customAction: (
              <Switch
                data-test="apollo-settings-dialerEnabled"
                aria-label="dialerEnabled"
                checked={dialer?.enabled ?? false}
                disabled={controlsDisabled}
                onCheckedChange={(enabled) =>
                  dialer &&
                  saveDialer.mutate(
                    { data: { ...dialer, enabled } },
                    onSaveError
                  )
                }
              />
            )
          },
          {
            title: t(`${PREFIX}.dialer.outcomes.title`),
            subTitle: apolloSubtitle(
              callOutcomesQuery,
              `${PREFIX}.dialer.outcomes.subtitle`
            ),
            customAction: (
              <DeferredMultiSelect
                fieldName="dialerOutcomes"
                values={dialer?.includeFacetIds ?? []}
                options={callOutcomeOptions(
                  callOutcomesQuery.data ?? [],
                  dialer?.includeFacetIds ?? []
                )}
                placeholder={t(`${PREFIX}.dialer.outcomes.placeholder`)}
                disabled={controlsDisabled}
                isLoading={callOutcomesQuery.isLoading}
                onSave={(includeFacetIds, onFailed) =>
                  isSettingsAdmin &&
                  dialer &&
                  saveDialer.mutate(
                    { data: { ...dialer, includeFacetIds } },
                    onDeferredSaveError(onFailed)
                  )
                }
              />
            )
          },
          {
            title: t(`${PREFIX}.dialer.createMissingRecords.title`),
            subTitle: t(`${PREFIX}.dialer.createMissingRecords.subtitle`),
            customAction: (
              <Switch
                data-test="apollo-settings-createMissingRecords"
                aria-label="createMissingRecords"
                checked={dialer?.createMissingRecords ?? true}
                disabled={controlsDisabled}
                onCheckedChange={(createMissingRecords) =>
                  dialer &&
                  saveDialer.mutate(
                    { data: { ...dialer, createMissingRecords } },
                    onSaveError
                  )
                }
              />
            )
          }
        ]}
      />
    </div>
  );
};
